import { useEffect, useMemo, useReducer, useRef, useState } from "react";

import {
  defaultTshirtGuide,
  type ProcessedTshirtImage,
} from "../shared/image-processing";
import { CapturePanel } from "./components/CapturePanel";
import { CollectionView } from "./components/CollectionView";
import { GuestScanDialog } from "./components/GuestScanDialog";
import {
  fetchCurrentAuthSession,
  fetchSavedTshirtCollection,
  isDevGuestSaveEnabled,
  signOut,
  saveProcessedTshirtImage,
  startGoogleSignIn,
  type AuthSession,
} from "./features/collection/api";
import { initialAppState, appReducer } from "./features/collection/state";
import {
  clearPendingTshirtImage,
  readPendingTshirtImage,
  savePendingTshirtImage,
} from "./features/collection/pending-save-store";
import { preloadMediaPipeAssets } from "./features/image-processing/preload-mediapipe-assets";
import { createImageWorkerProcessor } from "./features/image-processing/worker-client";

const processingMessages = [
  {
    delayMs: 900,
    message: "画像サイズを整えています",
  },
  {
    delayMs: 2400,
    message: "Tシャツ画像を書き出しています",
  },
  {
    delayMs: 5200,
    message: "服の輪郭を推定しています。初回はモデル読み込みに時間がかかります",
  },
  {
    delayMs: 18000,
    message: "まだ処理中です。通信が遅い場合はガイド型切り抜きに切り替わります",
  },
] as const;

const DebugSegmentationPanel = (props: {
  readonly originalObjectUrl: string | null;
  readonly image: ProcessedTshirtImage | null;
}) =>
  props.image ? (
    <section
      className="segmentation-debug"
      aria-labelledby="segmentation-debug-title"
    >
      <hgroup>
        <h2 id="segmentation-debug-title">Segmentation debug</h2>
        <p>{props.image.segmentationSource}</p>
      </hgroup>
      <div className="segmentation-debug__images">
        {props.originalObjectUrl ? (
          <figure>
            <img src={props.originalObjectUrl} alt="Original selected upload" />
            <figcaption>Original selected image</figcaption>
          </figure>
        ) : null}
        <figure>
          <img src={props.image.objectUrl} alt="Final transparent cutout" />
          <figcaption>Final cutout</figcaption>
        </figure>
        {props.image.maskPreviewObjectUrl ? (
          <figure>
            <img
              src={props.image.maskPreviewObjectUrl}
              alt="Segmentation mask preview"
            />
            <figcaption>Mask preview</figcaption>
          </figure>
        ) : null}
      </div>
      {props.image.segmentationDiagnostics.primaryErrorMessage ? (
        <p className="segmentation-debug__error">
          {props.image.segmentationDiagnostics.primaryErrorMessage}
        </p>
      ) : null}
      <pre>{JSON.stringify(props.image.segmentationDiagnostics, null, 2)}</pre>
    </section>
  ) : null;

export const App = () => {
  const [state, dispatch] = useReducer(appReducer, initialAppState);
  const [debugOriginalObjectUrl, setDebugOriginalObjectUrl] = useState<
    string | null
  >(null);
  const [authSession, setAuthSession] = useState<AuthSession | null>(null);
  const [pendingPreviewImage, setPendingPreviewImage] =
    useState<ProcessedTshirtImage | null>(null);
  const canSaveWithoutSession = useMemo(() => isDevGuestSaveEnabled(), []);
  const debugSegmentation = useMemo(
    () =>
      new URLSearchParams(window.location.search).get("debugSegmentation") ===
      "1",
    [],
  );
  const processor = useMemo(() => createImageWorkerProcessor(), []);
  const objectUrlRef = useRef<readonly string[]>([]);
  const debugOriginalObjectUrlRef = useRef<string | null>(null);
  const pendingPreviewObjectUrlRef = useRef<string | null>(null);
  const objectUrls = useMemo(
    () =>
      state.items.flatMap((item) =>
        item.images.front
          ? [
              ...(item.images.front.processedImage
                ? [item.images.front.processedImage.objectUrl]
                : []),
              ...(item.images.front.processedImage?.maskPreviewObjectUrl
                ? [item.images.front.processedImage.maskPreviewObjectUrl]
                : []),
            ]
          : [],
      ),
    [state.items],
  );

  const saveImageToCloud = (params: {
    readonly image: ProcessedTshirtImage;
    readonly savedAt: string;
    readonly onStatus: (message: string) => void;
    readonly afterSave?: () => Promise<void>;
  }): Promise<void> =>
    saveProcessedTshirtImage({
      image: params.image,
      onProgress: params.onStatus,
    })
      .then((tshirt) =>
        params.afterSave ? params.afterSave().then(() => tshirt) : tshirt,
      )
      .then((tshirt) => {
        dispatch({
          type: "image-added",
          image: params.image,
          now: params.savedAt,
          tshirt,
          saved: true,
        });
      });

  const restorePendingImageAfterSignIn = (
    onStatus: (message: string) => void,
  ): Promise<void> =>
    readPendingTshirtImage().then((image) =>
      image
        ? saveImageToCloud({
            image,
            savedAt: new Date().toISOString(),
            onStatus,
            afterSave: clearPendingTshirtImage,
          })
        : undefined,
    );

  useEffect(() => {
    const onStatus = (message: string) => {
      dispatch({
        type: "image-processing-progress",
        message,
      });
    };

    void fetchCurrentAuthSession()
      .then((session) => {
        setAuthSession(session);

        return session ? restorePendingImageAfterSignIn(onStatus) : undefined;
      })
      .catch((error: unknown) => {
        dispatch({
          type: "image-processing-failed",
          message:
            error instanceof Error ? error.message : "保存待ち画像の復元に失敗しました",
        });
      });
    dispatch({
      type: "collection-loading-started",
    });

    void fetchSavedTshirtCollection()
      .then((items) => {
        dispatch({
          type: "collection-loading-succeeded",
          items,
        });
      })
      .catch((error: unknown) => {
        dispatch({
          type: "collection-loading-failed",
          message:
            error instanceof Error
              ? error.message
              : "保存済みTシャツの読み込みに失敗しました",
        });
      });
  }, []);

  useEffect(() => {
    objectUrlRef.current = objectUrls;
  }, [objectUrls]);

  useEffect(() => {
    debugOriginalObjectUrlRef.current = debugOriginalObjectUrl;
  }, [debugOriginalObjectUrl]);

  useEffect(() => {
    pendingPreviewObjectUrlRef.current = pendingPreviewImage?.objectUrl ?? null;
  }, [pendingPreviewImage]);

  useEffect(
    () => () => {
      processor.dispose();
      objectUrlRef.current.map((objectUrl) => URL.revokeObjectURL(objectUrl));
      debugOriginalObjectUrlRef.current
        ? URL.revokeObjectURL(debugOriginalObjectUrlRef.current)
        : null;
      pendingPreviewObjectUrlRef.current
        ? URL.revokeObjectURL(pendingPreviewObjectUrlRef.current)
        : null;
    },
    [processor],
  );

  useEffect(() => {
    const onStatus = (message: string) => {
      dispatch({
        type: "image-processing-progress",
        message,
      });
    };

    debugSegmentation
      ? void preloadMediaPipeAssets({ onStatus })
          .catch((error: unknown) => {
            onStatus(
              error instanceof Error
                ? `MediaPipe事前取得に失敗しました: ${error.message}`
                : "MediaPipe事前取得に失敗しました",
            );
          })
          .then(() => processor.prepare({ onStatus }))
          .catch((error: unknown) => {
            onStatus(
              error instanceof Error
                ? `MediaPipe初期化に失敗しました: ${error.message}`
                : "MediaPipe初期化に失敗しました",
            );
          })
      : void processor.prepare().catch(() => undefined);
  }, [debugSegmentation, processor]);

  useEffect(() => {
    const timeoutHandles =
      state.capture.status === "processing"
        ? processingMessages.map((item) =>
            window.setTimeout(() => {
              dispatch({
                type: "image-processing-progress",
                message: item.message,
              });
            }, item.delayMs),
          )
        : [];

    return () => {
      timeoutHandles.map((timeoutHandle) => window.clearTimeout(timeoutHandle));
    };
  }, [state.capture.status]);

  const handleFileSelected = (file: File) => {
    dispatch({ type: "image-processing-started" });
    debugSegmentation
      ? setDebugOriginalObjectUrl((previousObjectUrl) => {
          previousObjectUrl ? URL.revokeObjectURL(previousObjectUrl) : null;

          return URL.createObjectURL(file);
        })
      : null;
    const onStatus = (message: string) => {
      dispatch({
        type: "image-processing-progress",
        message,
      });
    };

    void preloadMediaPipeAssets({ onStatus })
      .catch((error: unknown) => {
        onStatus(
          error instanceof Error
            ? `MediaPipe事前取得に失敗しました: ${error.message}`
            : "MediaPipe事前取得に失敗しました",
        );
      })
      .then(() =>
        processor.process({
          file,
          side: "front",
          guide: defaultTshirtGuide,
          onStatus,
          debugSegmentation,
        }),
      )
      .then((image) => {
        const savedAt = new Date().toISOString();

        return authSession || canSaveWithoutSession
          ? saveImageToCloud({
              image,
              savedAt,
              onStatus,
            })
          : savePendingTshirtImage(image).then(() => {
              setPendingPreviewImage((previousImage) => {
                previousImage
                  ? URL.revokeObjectURL(previousImage.objectUrl)
                  : null;

                return image;
              });
              dispatch({
                type: "image-preview-created",
              });
            });
      })
      .catch((error: unknown) => {
        dispatch({
          type: "image-processing-failed",
          message:
            error instanceof Error ? error.message : "画像処理に失敗しました",
        });
      });
  };

  const handleSignIn = () => {
    void startGoogleSignIn().catch((error: unknown) => {
      dispatch({
        type: "image-processing-failed",
        message:
          error instanceof Error ? error.message : "Googleログインに失敗しました",
      });
    });
  };

  const handleSignOut = () => {
    void signOut()
      .then(() => setAuthSession(null))
      .catch((error: unknown) => {
        dispatch({
          type: "image-processing-failed",
          message:
            error instanceof Error ? error.message : "ログアウトに失敗しました",
        });
      });
  };

  const handleDiscardPendingPreview = () => {
    const image = pendingPreviewImage;

    void clearPendingTshirtImage()
      .then(() => {
        image ? URL.revokeObjectURL(image.objectUrl) : null;
        setPendingPreviewImage(null);
      })
      .catch((error: unknown) => {
        dispatch({
          type: "image-processing-failed",
          message:
            error instanceof Error
              ? error.message
              : "保存待ち画像の削除に失敗しました",
        });
      });
  };

  return (
    <main className="app-shell">
      <CapturePanel
        captureStatus={state.capture.status}
        isAuthenticated={Boolean(authSession)}
        statusMessage={state.capture.message}
        onFileSelected={handleFileSelected}
        onSignIn={handleSignIn}
        onSignOut={handleSignOut}
      />
      <CollectionView
        items={state.items}
        statusMessage={state.collection.message}
      />
      <GuestScanDialog
        image={pendingPreviewImage}
        disabled={state.capture.status === "processing"}
        onSignIn={handleSignIn}
        onDiscard={handleDiscardPendingPreview}
      />
      {debugSegmentation ? (
        <DebugSegmentationPanel
          originalObjectUrl={debugOriginalObjectUrl}
          image={state.items[0]?.images.front?.processedImage ?? null}
        />
      ) : null}
    </main>
  );
};
